import api from '../../../../Component/axios_Interceptor/api';
import url from '../../../../Component/server';

type Purchase = { live_seq: number; product_seq: number; option_seq: number | null; order_cnt: number };
const inFlight = new Map<string, Promise<void>>();
const pending = new Map<string, Purchase & { idempotencyKey: string }>();
const storageKey = (payload: Purchase) => `fanbird:pending-purchase:${payload.live_seq}:${payload.product_seq}`;
const uncertain = () => new Error('구매 결과를 확인 중입니다. 다시 누르면 같은 요청의 결과를 확인합니다.');

/** Keep one purchase identity through rapid clicks, response loss and modal remounts. */
export function submitPurchase(payload: Purchase): Promise<void> {
  const key = storageKey(payload);
  const active = inFlight.get(key);
  if (active) return active;
  const operation = executePurchase(payload, key).finally(() => inFlight.delete(key));
  inFlight.set(key, operation);
  return operation;
}

async function executePurchase(payload: Purchase, key: string): Promise<void> {
  let request = pending.get(key);
  if (!request) {
    const stored = sessionStorage.getItem(key);
    if (stored) {
      try { request = JSON.parse(stored); } catch { throw uncertain(); }
      if (!request?.idempotencyKey) throw uncertain();
    }
  }
  if (request && ['live_seq', 'product_seq', 'option_seq', 'order_cnt'].some(field => (request as any)[field] !== (payload as any)[field])) {
    throw new Error('이 상품의 이전 구매 결과를 먼저 확인해주세요. 확인 중에는 옵션과 수량을 바꿔 다시 주문할 수 없습니다.');
  }
  const resuming = Boolean(request);
  if (!request) {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    request = { ...payload, idempotencyKey: Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('') };
    // Persist before sending. If storage is unavailable, fail before placing an order.
    sessionStorage.setItem(key, JSON.stringify(request));
  }
  pending.set(key, request);
  const finish = () => { pending.delete(key); sessionStorage.removeItem(key); };
  let response;
  try { response = await api.post(url.Communication_url.order, request, { timeout: 10000 }); }
  catch (error: any) {
    if ([400, 401, 403, 404, 409, 429].includes(error?.response?.status)) finish();
    throw error?.response ? error : uncertain();
  }
  if (response.data?.state === 'completed') { finish(); return; }
  if (!response.data?.jobId) throw uncertain();
  const deadline = Date.now() + 30000;
  while (Date.now() < deadline) {
    let status;
    try {
      status = (await api.get(`${url.Communication_url.order}-status`, { params: { jobId: response.data.jobId }, timeout: 10000 })).data;
    } catch (error: any) {
      if (error?.response?.status !== 404) throw uncertain();
    }
    if (status?.state === 'completed' && status?.result?.success === true) { finish(); return; }
    if (status?.state === 'failed') {
      finish();
      // A user retry of an old, uncertain purchase may proceed once the server
      // conclusively confirms its failure. A fresh rejection is shown immediately.
      if (resuming) return executePurchase(payload, key);
      throw new Error(status.failReason || '주문이 확정되지 않았습니다. 재고를 확인한 뒤 다시 시도해주세요.');
    }
    await new Promise(resolve => setTimeout(resolve, 500));
  }
  throw uncertain();
}
